import { useState, useEffect, useRef } from "react";

export const useSiteEdit = ({
  site,
  createSite,
  updateSite,
  cancelEditing,
  close,
}) => {
  const [name, setName] = useState(site?.name || '');
  const [description, setDescription] = useState(site?.description || '');
  const [siteSubjects, setSiteSubjects] = useState(site?.Subjects || []);
  const [siteUsers, setSiteUsers] = useState(site?.Users || []);
  // The server's values the fields were last in step with. A field whose draft
  // still equals them is untouched; one that differs holds unsaved typing.
  const synced = useRef({ id: site?.id, name: site?.name || '', description: site?.description || '' });

  // The page hands the drawer a new object whenever its list reloads or the
  // site changes on the server: a fresh `{}` for a new site, the same site with
  // new values after `site.changed`. Resetting on every object wiped a name
  // typed before the list arrived (the field is uncontrolled, so the screen
  // still showed it and Save sent no name, LF360-1981); never resetting would
  // keep stale values and save them over the server's. So: a different site
  // resets the fields, and for the same site an untouched field takes the new
  // value while one being edited keeps what was typed.
  useEffect(() => {
    if (!site) return;
    const previous = synced.current;
    const sameSite = site.id === previous.id;
    const next = { id: site.id, name: site.name || '', description: site.description || '' };
    synced.current = next;
    setName((draft) => (sameSite && draft !== previous.name ? draft : next.name));
    setDescription((draft) => (sameSite && draft !== previous.description ? draft : next.description));
  }, [site]);

  const onReset = () => {
    setName(site?.name);
    setDescription(site.description || '');
  };

  const updateSubjects = (values) => {
    setSiteSubjects(values);
  };

  const updateUsers = (values) => {
    setSiteUsers(values);
  };

  const create = () => {
    if (!site.id) {
      createSite({
        id: site.id,
        name,
        description,
        subjects: siteSubjects,
        users: siteUsers,
      });
    } else {
      updateSite(site.id, {
        ...site,
        name,
        description,
        subjects: siteSubjects,
        users: siteUsers,
      });
    }
  };

  const cancel = () => {
    if (site?.id) {
      onReset();
      cancelEditing();
      return;
    }
    close();
  };
  return {
    save: create,
    cancel: cancel,
    id: site?.id,
    isActive: site.status === 'active',
    name: {
      value: name,
      update: (value) => setName(value.target.value),
    },
    description: {
      value: description,
      update: (value) => {
        setDescription(value.target.value);
      },
    },
    subjects: {
      value: siteSubjects,
      onChange: (values) => {
        updateSubjects(values);
      },
      onCreate: (values) => {
        updateSubjects(values);
      },
    },
    users: {
      value: siteUsers,
      onChange: (values) => {
        updateUsers(values);
      },
      onCreate: (values) => {
        updateUsers(values);
      },
    },
  };
};
